// src/pages/Eligibility.js
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function Eligibility() {
  const navigate = useNavigate();

  return (
    <div className="bg-white text-[#0a2545] overflow-hidden">

      {/* 🌟 HERO SECTION */}
      <section
        className="relative py-32 px-6 text-center text-white bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,37,69,0.65), rgba(10,37,69,0.85)), url('/eb.png')",
        }}
      >
        <motion.h1
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-5xl md:text-6xl font-extrabold tracking-wide drop-shadow-xl"
        >
          Check Your Eligibility
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-gray-200"
        >
          Find out if you qualify for Canada’s easiest & fastest auto financing —
          even with **bad credit, no credit, or newcomer status**.
        </motion.p>
      </section>

      {/* 🌟 SECTION: WHY CHECK ELIGIBILITY */}
      <section className="py-24 px-6 md:px-20 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-4xl font-bold text-[#0a2545] mb-12"
        >
          Who Can Apply?
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            {
              title: "Bad Credit or No Credit?",
              text: "You can still qualify for a car loan with flexible approval options.",
            },
            {
              title: "New to Canada?",
              text: "Permanent residents, work permit holders, and students are eligible.",
            },
            {
              title: "Low Income or Self-Employed?",
              text: "We offer plans designed for flexible income or non-traditional jobs.",
            },
          ].map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2, duration: 0.7 }}
              className="p-10 rounded-3xl bg-gradient-to-br from-[#0a2545] to-[#0d5e63] text-white shadow-xl border border-teal-400/40 hover:shadow-teal-300/50 hover:scale-[1.03] transition-all"
            >
              <h3 className="text-2xl font-semibold mb-4 text-teal-300">
                {card.title}
              </h3>
              <p className="text-gray-200 text-sm leading-relaxed">{card.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 🌟 SECTION: ELIGIBILITY CHECKLIST */}
      <section className="py-20 px-6 md:px-24 bg-gradient-to-r from-[#0a2545] to-[#0d5e63] text-white">
        <motion.h2
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-4xl font-bold text-center mb-10"
        >
          Basic Eligibility Requirements
        </motion.h2>

        <ul className="max-w-3xl mx-auto space-y-6 text-lg">
          <motion.li
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white/10 p-5 rounded-xl border border-teal-300/30 shadow-md"
          >
            ✔ Must be 18+ years old
          </motion.li>

          <motion.li
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="bg-white/10 p-5 rounded-xl border border-teal-300/30 shadow-md"
          >
            ✔ Must have a steady monthly income
          </motion.li>

          <motion.li
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="bg-white/10 p-5 rounded-xl border border-teal-300/30 shadow-md"
          >
            ✔ Must live in Canada (any province)
          </motion.li>

          <motion.li
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="bg-white/10 p-5 rounded-xl border border-teal-300/30 shadow-md"
          >
            ✔ Citizenship: Canadian / PR / Work Permit / Student Visa / Visitor
          </motion.li>
        </ul>
      </section>

      {/* 🌟 CTA SECTION */}
      <section className="py-24 text-center bg-white">
        <motion.h2
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-4xl font-bold mb-6"
        >
          Ready to Check Your Eligibility?
        </motion.h2>
        <p className="text-gray-700 mb-8 max-w-xl mx-auto">
          Answer a few simple questions and get matched with the best financing
          options — it only takes **60 seconds**.
        </p>

        <motion.button
          onClick={() => navigate("/", { state: { openApply: true } })}
          whileHover={{ scale: 1.08 }}
          className="px-10 py-4 bg-teal-400 text-[#0a2545] font-semibold rounded-full shadow-xl hover:bg-white hover:text-[#0a2545] transition-all"
        >
          Check Eligibility Now
        </motion.button>
      </section>
    </div>
  );
}
